import { Box, InputAdornment } from '@mui/material';
import useMobile from 'src/shared/hooks/useMobile';
import { TextField } from 'mui-rff';
import { FormContentSection } from '../CategoriesGroup/Form/FormContentSection';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import GroupHeader from '../GroupHeader';
import { useStyles } from './styles';
import { useTranslation } from 'react-i18next';
import { useUpdateSocialBenefitMutation } from 'src/shared/api/flexflow';
import { useDispatch } from 'react-redux';
import { addToast } from 'src/old/actions';
import { toastTypes } from 'src/shared/components/Toast/ToastOptions';
import SocialBenefitEmployeeConfigForm, {
  type SocialBenefitEmployeeConfigFormValues
} from './SocialBenefitEmployeeConfigForm/SocialBenefitEmployeeConfigForm';
import { useIsHeroAdmin } from 'src/shared/hooks/useIsHeroAdmin';

type SocialBenefitGroupProps = {
  employeeId?: number;
  currencySymbol: string;
  sbWalletTotals?: {
    currentlyAvailable: { food: number; transport: number };
  };
  settings?: {
    sbFoodAccrual?: number;
    sbTransportAccrual?: number;
  };
};

const SocialBenefitGroup = ({ employeeId, currencySymbol, sbWalletTotals, settings }: SocialBenefitGroupProps) => {
  const classes = useStyles();
  const isMobile = useMobile();
  const { t } = useTranslation(['flexflow', 'actions']);
  const dispatch = useDispatch();
  const { isHeroAdmin } = useIsHeroAdmin();
  const [updateSocialBenefit, { isLoading: isUpdating }] = useUpdateSocialBenefitMutation();

  const inputProps = (startSymbol: string) => ({
    startAdornment: <InputAdornment position="start">{startSymbol}</InputAdornment>,
    endAdornment: (
      <InputAdornment position="end">
        <LockOutlinedIcon fontSize="small" sx={{ opacity: 0.4 }} />
      </InputAdornment>
    )
  });

  const accumulatedFood = sbWalletTotals?.currentlyAvailable?.food ?? 0;
  const accumulatedTransport = sbWalletTotals?.currentlyAvailable?.transport ?? 0;
  const configFood = settings?.sbFoodAccrual ?? 0;
  const configTransport = settings?.sbTransportAccrual ?? 0;
  const handleSave = async (values: SocialBenefitEmployeeConfigFormValues) => {
    if (!isHeroAdmin || !employeeId) return;

    try {
      const parsedFood = Number(values.sbFoodAccrual);
      const parsedTransport = Number(values.sbTransportAccrual);

      await updateSocialBenefit({
        employeeId,
        sbFoodAccrual: parsedFood,
        sbTransportAccrual: parsedTransport
      }).unwrap();

      dispatch(
        addToast({
          ...toastTypes.success,
          defaultMessage: t('toasts.updated', { ns: 'actions' })
        })
      );
    } catch {
      dispatch(
        addToast({
          ...toastTypes.error,
          defaultMessage: t('toasts.errorDefault.defaultMessage', { ns: 'actions' })
        })
      );
    }
  };

  return (
    <Grid2 container>
      <GroupHeader className={classes.tooltipWrapper} marginTop={32}>
        {t('social benefit', { ns: 'flexflow' })}
      </GroupHeader>

      <Grid2 mb={4} xs={12} md={10} lg={6}>
        <div>
          <Grid2 xs={12} sx={{ w: '100%', maxWidth: '600px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormContentSection
                isMobile={isMobile}
                title={t('socialBenefitEmployeeDetail.balance.title', { ns: 'flexflow' })}
                description={t('socialBenefitEmployeeDetail.balance.description', { ns: 'flexflow' })}
              >
                <TextField
                  disabled
                  label={t('socialBenefitEmployeeDetail.balance.food', { ns: 'flexflow' })}
                  name="sbAccumulatedFood"
                  value={accumulatedFood}
                  sx={{ flex: 1 }}
                  InputProps={inputProps(currencySymbol)}
                  placeholder="-"
                />
                <TextField
                  disabled
                  label={t('socialBenefitEmployeeDetail.balance.transport', { ns: 'flexflow' })}
                  name="sbAccumulatedTransport"
                  value={accumulatedTransport}
                  sx={{ flex: 1 }}
                  InputProps={inputProps(currencySymbol)}
                  placeholder="-"
                />
              </FormContentSection>
              <FormContentSection
                isMobile={isMobile}
                title={t('socialBenefitEmployeeDetail.configuration.title', { ns: 'flexflow' })}
                description={t('socialBenefitEmployeeDetail.configuration.description', { ns: 'flexflow' })}
              >
                <SocialBenefitEmployeeConfigForm
                  currencySymbol={currencySymbol}
                  sbFoodAccrual={configFood}
                  sbTransportAccrual={configTransport}
                  onSubmit={handleSave}
                  isSaving={isUpdating}
                  isReadOnly={!isHeroAdmin}
                />
              </FormContentSection>
            </Box>
          </Grid2>
        </div>
      </Grid2>
    </Grid2>
  );
};

export default SocialBenefitGroup;
